import { useEffect, useState } from 'react';
import { CookieJar } from '../CookieJar';
import { api, Attempt, AttemptHistoryRow, Cookies, Dispute, MissedQuestion, ProgressSummary } from '../api';
import { Icon } from '../Icon';
import { useSession } from '../auth/session';
import { PreferencesPanel } from '../a11y/preferences';
import { reopenAttempt } from './Resume';

function pct(n: number) { return `${Math.round(n * 100)}%`; }

function AccuracyBar({ value, label }: { value: number; label: string }) {
  return (
    <div className="bar-meter" role="img" aria-label={`${label}: ${pct(value)}`}>
      <span style={{ width: pct(value) }} />
    </div>
  );
}

export function Profile({ onBack, onDrill, onResume }: {
  onBack: () => void; onDrill: (bankVersionId: string) => void; onResume: (attempt: Attempt) => void;
}) {
  const { learner, signOut, setLearner } = useSession();
  const [progress, setProgress] = useState<ProgressSummary | null>(null);
  const [history, setHistory] = useState<AttemptHistoryRow[]>([]);
  const [missed, setMissed] = useState<MissedQuestion[]>([]);
  const [cookies, setCookies] = useState<Cookies | null>(null);
  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [name, setName] = useState(learner?.displayName ?? '');
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      api.get<ProgressSummary>('/me/progress'),
      api.get<AttemptHistoryRow[]>('/me/attempts'),
      api.get<MissedQuestion[]>('/me/incorrect'),
      api.get<Cookies>('/me/cookies'),
      api.get<Dispute[]>('/me/disputes'),
    ]).then(([p, h, m, c, d]) => {
      setProgress(p); setHistory(h); setMissed(m); setCookies(c); setDisputes(d);
    }).catch((e) => setErr(e.message));
  }, []);

  // The history row may be stale: the attempt could have been finished in
  // another tab since this page loaded, so ask the server before reopening.
  const [resumeErr, setResumeErr] = useState<string | null>(null);
  async function resume(attemptId: string) {
    setResumeErr(null);
    const found = await reopenAttempt(attemptId);
    if (found.kind === 'open') onResume(found.attempt);
    else setResumeErr(found.kind === 'finished'
      ? 'That test has already finished. Reload this page to see its result.'
      : 'Could not reach the server to reopen that test. It is still running — try again in a moment.');
  }

  async function saveName() {
    setMsg(null); setErr(null);
    try {
      const updated = await api.put<any>('/me/profile', { displayName: name });
      setLearner(updated);
      setMsg('Display name saved.');
    } catch (e: any) { setErr(e.message); }
  }

  const overall = progress?.totals.answered
    ? progress.totals.correct / progress.totals.answered : 0;

  return (
    <>
      <button className="btn-ghost btn-back" onClick={onBack}><Icon name="arrow-left" />Back to banks</button>
      <h1>Your progress</h1>
      <p className="muted">
        Signed in as {learner?.email}. These are raw practice figures across your attempts —
        not a scaled score, and not a prediction of any examination outcome.
      </p>

      {err && <div className="notice notice--warn" role="alert">{err}</div>}

      {cookies && <div style={{ margin: '1.5rem 0' }}><CookieJar cookies={cookies} /></div>}

      {disputes.length > 0 && (
        <section>
          <h2>Questions you reported</h2>
          <ul className="submission-list">
            {disputes.map((d) => (
              <li key={d.id} className="card card--plain">
                <p style={{ marginTop: 0 }}>{d.stem}</p>
                <p className="muted" style={{ marginBottom: 0 }}>
                  You proposed <strong>{d.proposedResponse.join(', ').toUpperCase()}</strong> ·{' '}
                  <strong>
                    {d.state === 'open' ? 'Awaiting review'
                      : d.state === 'accepted' ? '✓ Accepted — 1 cookie'
                      : 'Not accepted'}
                  </strong>
                  {d.resolutionNote ? ` — ${d.resolutionNote}` : ''}
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}

      {progress && progress.totals.answered === 0 && (
        <div className="notice">
          You have not answered any questions yet. Finish a session and your performance by topic
          will appear here.
        </div>
      )}

      {progress && progress.totals.answered > 0 && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>Overall</h2>
          <p style={{ fontSize: '1.4rem', margin: '0 0 0.5rem' }}>
            <strong>{progress.totals.correct} of {progress.totals.answered}</strong> correct
            <span className="muted" style={{ fontSize: '1rem' }}> ({pct(overall)})</span>
          </p>
          <AccuracyBar value={overall} label="Overall accuracy" />
        </div>
      )}

      {progress?.subjects.map((s) => (
        <section key={s.packId} className="card">
          <h2 style={{ marginTop: 0 }}>{s.subject}</h2>
          <p className="muted">
            {s.completedAttempts} completed session{s.completedAttempts === 1 ? '' : 's'} ·{' '}
            {s.correct} of {s.answered} correct ({pct(s.accuracy)})
          </p>

          {s.focusTopics.length > 0 ? (
            <div className="notice notice--warn">
              <h3 style={{ marginTop: 0 }}>Topics to work on</h3>
              <ol style={{ margin: 0, paddingLeft: '1.25rem' }}>
                {s.focusTopics.map((f) => (
                  <li key={f.topicId} style={{ marginBottom: '0.4rem' }}>
                    <strong>{f.topicName}</strong> — {f.reason}
                  </li>
                ))}
              </ol>
            </div>
          ) : (
            <p className="muted">
              Nothing stands out yet. A topic appears here once you have answered at least three
              of its questions and are getting fewer than 70% right.
            </p>
          )}

          <h3>Every topic</h3>
          <div className="scroll-x">
            <table>
              <caption className="visually-hidden">Performance by topic for {s.subject}</caption>
              <thead>
                <tr>
                  <th scope="col">Topic</th><th scope="col">Correct</th>
                  <th scope="col">Wrong</th><th scope="col">Answered</th><th scope="col">Accuracy</th>
                </tr>
              </thead>
              <tbody>
                {s.topics.map((t) => (
                  <tr key={t.topicId}>
                    <th scope="row" style={{ fontWeight: 400 }}>{t.topicName}</th>
                    <td>{t.correct}</td><td>{t.incorrect}</td><td>{t.answered}</td>
                    <td>{pct(t.accuracy)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}

      {missed.length > 0 && (
        <section className="card">
          <h2 style={{ marginTop: 0 }}>Questions you have got wrong ({missed.length})</h2>
          <p className="muted">Each one, with the correct answer and why.</p>
          {missed.slice(0, 25).map((m) => (
            <details key={m.questionId} style={{ borderTop: '1px solid var(--border)', padding: '0.75rem 0' }}>
              <summary style={{ cursor: 'pointer', minHeight: '2.75rem', display: 'flex', alignItems: 'center' }}>
                {m.stem}
              </summary>
              <p className="muted" style={{ marginTop: '0.75rem' }}>
                You answered <strong>{m.selectedResponse.join(', ').toUpperCase()}</strong> ·
                correct answer <strong>{m.correctResponse.join(', ').toUpperCase()}</strong>
              </p>
              {m.rationale && <p>{m.rationale}</p>}
            </details>
          ))}
          {missed.length > 25 && <p className="muted">Showing the 25 most recent.</p>}
        </section>
      )}

      <section className="card">
        <h2 style={{ marginTop: 0 }}>Attempt history</h2>
        {/* Next to the button that caused it, not at the top of the page. */}
        {resumeErr && <div className="notice notice--warn" role="alert">{resumeErr}</div>}
        {history.length === 0 && <p className="muted">No attempts yet.</p>}
        {history.length > 0 && (
          <div className="scroll-x">
            <table>
              <caption className="visually-hidden">Your attempts</caption>
              <thead>
                <tr>
                  <th scope="col">Date</th><th scope="col">Subject</th><th scope="col">Mode</th>
                  <th scope="col">Score</th><th scope="col">State</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.attemptId}>
                    <td>{new Date(h.startedAt).toLocaleDateString()}</td>
                    <td>{h.subject} <span className="muted">{h.edition}</span></td>
                    <td>{h.mode === 'study' ? 'Study' : 'Timed'}</td>
                    <td>{h.rawScore !== null ? `${h.rawScore}/${h.denominator}` : '—'}</td>
                    <td>
                      {h.state.replace('_', ' ')}
                      {h.state === 'in_progress' && (
                        <button style={{ marginLeft: '0.5rem' }} onClick={() => void resume(h.attemptId)}>
                          <Icon name="play" />Resume
                          <span className="visually-hidden"> {h.subject} test</span>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="card">
        <h2 style={{ marginTop: 0 }}>Account</h2>
        <label htmlFor="display-name">Display name</label>
        <input id="display-name" type="text" value={name} onChange={(e) => setName(e.target.value)} />
        <div className="btn-row" style={{ marginTop: '0.75rem' }}>
          <button onClick={saveName}>Save name</button>
          <button onClick={() => { void signOut(); onBack(); }}>Sign out</button>
        </div>
        {msg && <p className="muted" role="status" style={{ marginTop: '0.75rem' }}>{msg}</p>}
      </section>

      <details className="card card--plain">
        <summary style={{ cursor: 'pointer', minHeight: '2.75rem', display: 'flex', alignItems: 'center' }}>
          Reading and display options
        </summary>
        <div style={{ marginTop: '1rem' }}><PreferencesPanel /></div>
      </details>
    </>
  );
}
